/**
 * TenderUploadPanel — TN-IPDS Tender Intake
 *
 * Upload a tender PDF, display AI extraction confidence,
 * preview extracted fields, and submit to the Tender Intake agent.
 *
 * API: POST /v1/tn-ipds/tenders
 */
import React, { useState, useRef } from 'react';
import { API_BASE } from '../../config/apiConfig';

const s = {
  container: { fontFamily: 'sans-serif', maxWidth: 800, margin: '0 auto', padding: 24 },
  title: { fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 },
  subtitle: { fontSize: 13, color: '#666', marginBottom: 24 },
  uploadZone: {
    border: '2px dashed #4f46e5', borderRadius: 10, padding: '40px 24px',
    textAlign: 'center', cursor: 'pointer', background: '#f9f8ff',
    transition: 'background 0.15s',
  },
  uploadZoneActive: { background: '#ede9fe', borderColor: '#7c3aed' },
  btn: {
    padding: '10px 20px', borderRadius: 6, border: 'none',
    cursor: 'pointer', fontSize: 14, fontWeight: 600,
  },
  btnPrimary: { background: '#4f46e5', color: '#fff' },
  btnSecondary: { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' },
  card: {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
    padding: 20, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  fieldRow: { display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap' },
  fieldLabel: { fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 },
  fieldValue: { fontSize: 14, color: '#1a1a2e' },
  confidenceBadge: (score) => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: 20,
    fontSize: 12, fontWeight: 700,
    background: score >= 0.85 ? '#d1fae5' : score >= 0.65 ? '#fef3c7' : '#fee2e2',
    color: score >= 0.85 ? '#065f46' : score >= 0.65 ? '#92400e' : '#991b1b',
  }),
  error: { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 12 },
  success: { background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px', color: '#16a34a', fontSize: 13, marginBottom: 12 },
  progressBar: { height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden', marginTop: 8 },
  progressFill: (pct) => ({ height: '100%', background: '#4f46e5', width: `${pct}%`, transition: 'width 0.3s' }),
};

const TenderUploadPanel = ({ clientId, apiBase }) => {
  const base = apiBase || API_BASE || '/v1';
  const fileRef = useRef();

  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [extracted, setExtracted] = useState(null);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.match(/\.(pdf|docx)$/i)) {
      setError('Only PDF or DOCX files are accepted.');
      return;
    }
    setFile(f);
    setError('');
    setExtracted(null);
    setSubmitted(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    setProgress(10);

    const formData = new FormData();
    formData.append('file', file);
    if (clientId) formData.append('client_id', clientId);

    try {
      setProgress(40);
      const res = await fetch(`${base}/tn-ipds/tenders/extract`, {
        method: 'POST',
        body: formData,
      });
      setProgress(80);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Upload failed (${res.status})`);
      }
      const data = await res.json();
      setExtracted(data);
      setProgress(100);
    } catch (err) {
      setError(err.message);
      setProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!extracted) return;
    setUploading(true);
    setError('');
    try {
      const res = await fetch(`${base}/tn-ipds/tenders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: clientId,
          extracted_fields: extracted.fields,
          source_file: file?.name,
          extraction_confidence: extracted.confidence,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Submission failed (${res.status})`);
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const conf = extracted?.confidence || 0;

  return (
    <div style={s.container}>
      <h2 style={s.title}>📋 Tender Upload & Intake</h2>
      <p style={s.subtitle}>
        Upload a tender PDF or DOCX. The AI will extract fields, show confidence,
        and allow you to review before submitting to the procurement system.
      </p>

      {error && <div style={s.error} role="alert">⚠ {error}</div>}
      {submitted && (
        <div style={s.success}>
          ✅ Tender submitted successfully. It has been queued for risk scanning.
        </div>
      )}

      {/* Upload zone */}
      {!extracted && (
        <div
          style={{ ...s.uploadZone, ...(dragging ? s.uploadZoneActive : {}) }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          role="button"
          tabIndex={0}
          aria-label="Upload tender document"
          onKeyDown={(e) => e.key === 'Enter' && fileRef.current?.click()}
        >
          <div style={{ fontSize: 40, marginBottom: 8 }}>📄</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#4f46e5' }}>
            {file ? file.name : 'Drop tender PDF/DOCX here or click to browse'}
          </div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            Supported: PDF, DOCX — max 20 MB
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.docx"
            style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files[0])}
          />
        </div>
      )}

      {file && !extracted && (
        <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
          <button
            style={{ ...s.btn, ...s.btnPrimary, opacity: uploading ? 0.6 : 1 }}
            onClick={handleUpload}
            disabled={uploading}
          >
            {uploading ? '⏳ Extracting…' : '🔍 Extract Fields'}
          </button>
          <button
            style={{ ...s.btn, ...s.btnSecondary }}
            onClick={() => { setFile(null); setProgress(0); }}
            disabled={uploading}
          >
            Clear
          </button>
        </div>
      )}

      {uploading && (
        <div style={s.progressBar}>
          <div style={s.progressFill(progress)} />
        </div>
      )}

      {/* Extraction preview */}
      {extracted && !submitted && (
        <div style={s.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
              🤖 Extracted Fields — {file?.name}
            </h3>
            <span style={s.confidenceBadge(conf)}>
              Confidence: {Math.round(conf * 100)}%
            </span>
          </div>

          {conf < 0.65 && (
            <div style={{ ...s.error, marginBottom: 12 }}>
              ⚠ Low extraction confidence. Please review all fields carefully before submitting.
            </div>
          )}

          <div style={s.fieldRow}>
            {Object.entries(extracted.fields || {}).map(([key, value]) => (
              <div key={key} style={{ minWidth: 200, flex: '1 1 200px' }}>
                <div style={s.fieldLabel}>{key.replace(/_/g, ' ')}</div>
                <div style={s.fieldValue}>{String(value ?? '—')}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button
              style={{ ...s.btn, ...s.btnPrimary, opacity: uploading ? 0.6 : 1 }}
              onClick={handleSubmit}
              disabled={uploading}
            >
              {uploading ? '⏳ Submitting…' : '✅ Submit to Procurement System'}
            </button>
            <button
              style={{ ...s.btn, ...s.btnSecondary }}
              onClick={() => { setExtracted(null); setFile(null); setProgress(0); }}
            >
              ↺ Upload Different File
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenderUploadPanel;
